import React, { ReactNode } from "react"
import { Control } from "react-hook-form"
import {
  BadgeElement,
  BadgeElementType,
  BadgeQRFormat,
  BadgeTextSource,
} from "sharedTypes/badge"
import Icon from "components/elements/Icon"
import { EventData, GuestData } from "./utils/badgeDataResolver"
import { BADGE_FONTS, BADGE_FONT_WEIGHTS } from "./utils/badgeFonts"
import { QR_MIN_SIZES_MM } from "./utils/badgeHelpers"
import {
  CircleIcon,
  RectangleEllipsisIcon,
  RectangleIcon,
  TypeIcon,
} from "./Icons"
import BadgeField from "./Elements/BadgeField"
import BadgeImage from "./Elements/BadgeImage"
import BadgeLine from "./Elements/BadgeLine"
import BadgeOval from "./Elements/BadgeOval"
import BadgeQRCode from "./Elements/BadgeQRCode"
import BadgeRectangle from "./Elements/BadgeRectangle"
import BadgeText from "./Elements/BadgeText"
import { BadgeElementFormValues } from "./Panels/EditorPanel/BadgeSidePanelForm"
import BadgeImageFields from "./Panels/EditorPanel/Fields/BadgeImageFields"
import BadgeLineFields from "./Panels/EditorPanel/Fields/BadgeLineFields"
import BadgeOvalFields from "./Panels/EditorPanel/Fields/BadgeOvalFields"
import BadgeQRCodeFields from "./Panels/EditorPanel/Fields/BadgeQRCodeFields"
import BadgeRectangleFields from "./Panels/EditorPanel/Fields/BadgeRectangleFields"
import BadgeTextFields from "./Panels/EditorPanel/Fields/BadgeTextFields"

type ElementOf<T extends BadgeElementType> = Extract<BadgeElement, { type: T }>

export type ElementRenderContext = {
  scaleFactor: number
  guestData?: GuestData
  eventData?: EventData
  /** Inline text editing, for text elements in the editor. */
  isEditing: boolean
  onTextChange?: (text: string) => void
  onStopEditing?: () => void
}

type FieldsProps = {
  control: Control<BadgeElementFormValues>
  eventId: string
  /** For several elements: only their style, not what each one says. */
  group: boolean
}

export type ElementTypeDefinition<T extends BadgeElementType = BadgeElementType> = {
  type: T
  /** The toolbar label. */
  name: string
  icon: React.ReactElement
  group: "shapes" | "elements"
  /** Size of a new element, in mm. */
  size: { width: number; height: number }
  /** New elements get a transparent background fill. */
  transparentFill: boolean
  createData: () => ElementOf<T>["data"]
  render: (element: ElementOf<T>, context: ElementRenderContext) => ReactNode
  /** The element's own fields in the element form. */
  Fields: (props: FieldsProps) => ReactNode
}

const defineElementType = <T extends BadgeElementType>(definition: ElementTypeDefinition<T>) => (
  definition
)

const textStyle = () => ({
  font: BADGE_FONTS.Arial,
  fontSize: 12,
  weight: BADGE_FONT_WEIGHTS.Regular,
  color: "#000000" as const,
  align: "left" as const,
  verticalAlign: "center" as const,
  bold: false,
  italic: false,
  textDecoration: "none" as const,
  textTransform: "none" as const,
})

const shapeData = () => ({
  strokeColor: "#000000" as const,
  fillColor: "transparent" as const,
  thickness: 0.8,
  style: "solid" as const,
})

/** Every badge element type, in toolbar order: how it's created, drawn and edited. */
const BADGE_ELEMENT_TYPES = [
  defineElementType({
    type: BadgeElementType.RECTANGLE,
    name: "Rectangle",
    icon: <RectangleIcon width={24} height={24} strokeWidth={1.5} />,
    group: "shapes",
    size: { width: 30, height: 20 },
    transparentFill: true,
    createData: shapeData,
    render: (element, { scaleFactor }) => (
      <BadgeRectangle element={element} scaleFactor={scaleFactor} />
    ),
    Fields: ({ control }) => <BadgeRectangleFields control={control} />,
  }),
  defineElementType({
    type: BadgeElementType.OVAL,
    name: "Oval",
    icon: <CircleIcon width={24} height={24} strokeWidth={1.5} />,
    group: "shapes",
    size: { width: 30, height: 20 },
    transparentFill: true,
    createData: shapeData,
    render: (element, { scaleFactor }) => <BadgeOval element={element} scaleFactor={scaleFactor} />,
    Fields: ({ control }) => <BadgeOvalFields control={control} />,
  }),
  defineElementType({
    type: BadgeElementType.LINE,
    name: "Line",
    icon: <Icon icon="email-block-divider" size={6} />,
    group: "shapes",
    size: { width: 40, height: 10 },
    transparentFill: true,
    createData: () => ({ color: "#000000", thickness: 0.8, style: "solid" }),
    render: (element, { scaleFactor }) => <BadgeLine element={element} scaleFactor={scaleFactor} />,
    Fields: ({ control }) => <BadgeLineFields control={control} />,
  }),
  defineElementType({
    type: BadgeElementType.TEXT,
    name: "Text",
    icon: <TypeIcon width={24} height={24} strokeWidth={1.5} />,
    group: "elements",
    size: { width: 30, height: 8 },
    transparentFill: false,
    // Starts empty, ready for typing
    createData: () => ({ text: "", ...textStyle() }),
    render: (element, {
      scaleFactor, isEditing, onTextChange, onStopEditing,
    }) => (
      <BadgeText
        element={element}
        scaleFactor={scaleFactor}
        isEditing={isEditing}
        onTextChange={onTextChange}
        onStopEditing={onStopEditing}
      />
    ),
    Fields: ({ control, group }) => (
      <BadgeTextFields control={control} variant="static" group={group} />
    ),
  }),
  defineElementType({
    type: BadgeElementType.FIELD,
    name: "Field",
    icon: <RectangleEllipsisIcon width={24} height={24} strokeWidth={1.5} />,
    group: "elements",
    size: { width: 35, height: 8 },
    transparentFill: false,
    createData: () => ({ source: BadgeTextSource.GUEST_FULL_NAME, ...textStyle() }),
    render: (element, { scaleFactor, guestData, eventData }) => (
      <BadgeField
        element={element}
        scaleFactor={scaleFactor}
        guestData={guestData}
        eventData={eventData}
      />
    ),
    Fields: ({ control, group }) => (
      <BadgeTextFields control={control} variant="field" group={group} />
    ),
  }),
  defineElementType({
    type: BadgeElementType.IMAGE,
    name: "Image",
    icon: <Icon icon="email-block-image" size={6} />,
    group: "elements",
    size: { width: 20, height: 20 },
    transparentFill: false,
    createData: () => ({ url: "", fit: "contain" }),
    render: (element) => <BadgeImage element={element} />,
    Fields: ({ control, eventId, group }) => (
      <BadgeImageFields control={control} eventId={eventId} group={group} />
    ),
  }),
  defineElementType({
    type: BadgeElementType.QR_CODE,
    name: "QR",
    icon: <Icon icon="email-block-qr-code" size={6} />,
    group: "elements",
    // The vCard minimum, so switching the format doesn't have to grow it
    size: { width: QR_MIN_SIZES_MM.vcard, height: QR_MIN_SIZES_MM.vcard },
    transparentFill: true,
    createData: () => ({ format: BadgeQRFormat.GUEST_ID }),
    render: (element) => <BadgeQRCode element={element} />,
    Fields: ({ control }) => <BadgeQRCodeFields control={control} />,
  }),
] as const

export const getElementType = (type: BadgeElementType) => (
  BADGE_ELEMENT_TYPES.find((definition) => definition.type === type) as
    ElementTypeDefinition | undefined
)

/** Draws an element with its type's renderer. */
export const renderElement = (element: BadgeElement, context: ElementRenderContext) => (
  getElementType(element.type)?.render(element as never, context) ?? null
)

export default BADGE_ELEMENT_TYPES
